import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";

// Images
import { Grass } from "@assets";

// Helpers
import { useDarkMode } from "@hooks/dark-mode";
import { useTranslation } from "react-i18next";

import NoAuth from "../no-auth";

const ContactContent = () => {
  const { t } = useTranslation();
  const form = useRef<HTMLFormElement | null>(null);
  const [status, setStatus] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Disable page scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (showSuccessModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalOverflow || "";
    }
    return () => {
      document.body.style.overflow = originalOverflow || "";
    };
  }, [showSuccessModal]);

  const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("Sending...");

    // populate time field
    const currentForm = form.current as HTMLFormElement | null;
    const timeInput = currentForm?.elements.namedItem("time") as HTMLInputElement | null;
    if (timeInput) timeInput.value = new Date().toLocaleString();

    emailjs
      .sendForm(
        "service_drrrcq3",
        "template_i0g99at",
        form.current as HTMLFormElement,
        "EhBDZQvli8gtvkvPd"
      )
      .then(
        () => {
          setStatus("✅ Message sent successfully!");
          form.current?.reset();
          setShowSuccessModal(true);
        },
        (error) => {
          // eslint-disable-next-line no-console
          console.error(error);
          setStatus("❌ Failed to send. Please try again.");
        }
      );
  };


  // Dark Mode
  const dark = useDarkMode();

  return (
    <section
      style={{
        backgroundImage: `url(${Grass})`,
        backgroundSize: "cover",
        backgroundColor: dark.dark
          ? "rgba(0, 0, 0, 0.6)"
          : "rgba(255, 255, 255, 0.8)",
        backgroundBlendMode: "multiply"
      }}
      className="bg-bg h-screen"
    >
      <div className="py-8 lg:py-16 px-4 mx-auto max-w-screen-md">
        <motion.h2
          className="mb-4 text-4xl tracking-tight font-extrabold text-center text-white dark:text-black"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {t('contact.title')}
        </motion.h2>
        <motion.p
          className="lg:mb-16 font-light text-center text-gray-300 dark:text-gray-400 sm:text-xl"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {t('contact.description')}
          <p className="mb-6 text-center text-gray-200 dark:text-gray-400">
            {t('contact.directEmailPrefix')}{' '}
            <a href="mailto:info@drivn3.com" className="underline hover:no-underline">
              info@drivn3.com
            </a>
          </p>
        </motion.p>
        <motion.form
          ref={form}
          onSubmit={sendEmail}
          className="space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <input type="hidden" name="time" />
          <div>
            <label className="block mb-2 text-sm font-medium text-white dark:text-black">
              {t('contact.email')}
            </label>
            <input
              name="name"
              type="email"
              id="email"
              className="block p-3 w-full text-sm bg-secBg rounded-lg border border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:placeholder-gray-400 dark:shadow-sm-light"
              placeholder={t('contact.emailPlaceholder')}
              required
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-white dark:text-black">
              {t('contact.subject')}
            </label>
            <input
              name="title"
              type="text"
              id="subject"
              className="block p-3 w-full text-sm bg-secBg rounded-lg border border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:placeholder-gray-400 dark:shadow-sm-light"
              placeholder={t('contact.subjectPlaceholder')}
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block mb-2 text-sm font-medium text-white dark:text-black">
              {t('contact.message')}
            </label>
            <textarea
              name="message"
              id="message"
              rows={6}
              className="block p-2.5 w-full text-sm text-black bg-secBg rounded-lg shadow-sm border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:placeholder-gray-400 "
              placeholder={t('contact.messagePlaceholder')}
            ></textarea>
          </div>
          <button
            type="submit"
            className="py-3 px-5 text-sm font-medium w-full text-center text-bg rounded-lg bg-main hover:bg-secBg focus:outline-none transition-all"
          >
            {t('contact.submit')}
          </button>
          {status && (
            <p className="text-center text-sm text-white pt-2">{status}</p>
          )}
        </motion.form>
        {showSuccessModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="absolute inset-0 bg-black/80"
              onClick={() => setShowSuccessModal(false)}
            />
            <div className="relative z-10 w-[90%] max-w-md rounded-xl bg-secBg p-6 shadow-2xl border border-gray-300">
              <h3 className="text-lg font-semibold text-black mb-2">
                {t('contact.successTitle', { defaultValue: 'Message Sent' })}
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                {t('contact.successBody', { defaultValue: 'Thank you for reaching out! We\'ll email you back as soon as possible.' })}
              </p>
              <button
                type="button"
                className="w-full py-2.5 px-4 rounded-lg bg-main text-bg hover:text-black hover:bg-secBg transition-all"
                onClick={() => setShowSuccessModal(false)}
                autoFocus
              >
                {t('contact.successCta', { defaultValue: 'Got it' })}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const Contact = () => {
  const { t } = useTranslation();
  return <NoAuth component={ContactContent} title={t('contact.pageTitle')} />;
};

export default Contact;
